import React, { useState, useEffect } from 'react';
import { planningAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const DIARY_TYPES = [
  { value: 'event', label: 'Event', color: '#1E5A8A' },
  { value: 'court', label: 'Court Date', color: '#4A1D2A' },
  { value: 'political', label: 'Political', color: '#B3261E' },
  { value: 'sports', label: 'Sports', color: '#1F7A5A' },
  { value: 'anniversary', label: 'Anniversary', color: '#B89B5E' },
  { value: 'press_conference', label: 'Press Conference', color: '#9A6B1A' },
];

export default function AdminPlanning() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ date: '', title: '', description: '', type: 'event', priority: 'standard' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try { const res = await planningAPI.diary({}); setEntries(res.data.entries || []); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const createEntry = async () => {
    if (!form.title || !form.date) { toast.error('Title and date required'); return; }
    try {
      await planningAPI.addEntry(form);
      toast.success('Entry added');
      setShowDialog(false); setForm({ date: '', title: '', description: '', type: 'event', priority: 'standard' });
      loadData();
    } catch { toast.error('Failed'); }
  };

  const deleteEntry = async (id) => {
    try { await planningAPI.deleteEntry(id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-planning-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Calendar className="w-6 h-6 text-[#B89B5E]" /> News Diary & Planning</h1>
          <p className="text-sm text-[#5B616E] mt-1">Track upcoming events, court dates, and editorial opportunities</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="add-diary-entry-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> Add Entry</Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : (
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-6">
            {entries.length > 0 ? (
              <div className="space-y-3">
                {entries.map(e => {
                  const typeInfo = DIARY_TYPES.find(t => t.value === e.type);
                  return (
                    <div key={e.id} className="flex items-center gap-4 p-3 rounded-lg border border-[#E7E0D6] hover:bg-[#FBF7EF] transition-colors group" data-testid="diary-entry">
                      <div className="w-1 h-10 rounded-full flex-shrink-0" style={{ background: typeInfo?.color || '#5B616E' }} />
                      <div className="w-20 text-center flex-shrink-0">
                        <p className="text-xs font-medium">{e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</p>
                        <p className="text-[10px] text-[#5B616E]">{e.date ? new Date(e.date).toLocaleDateString('en-US', { weekday: 'short' }) : ''}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{e.title}</p>
                        {e.description && <p className="text-xs text-[#5B616E] mt-0.5 truncate">{e.description}</p>}
                      </div>
                      <Badge variant="outline" className="text-[10px]" style={{ borderColor: typeInfo?.color, color: typeInfo?.color }}>{typeInfo?.label || e.type}</Badge>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-[#B3261E]" onClick={() => deleteEntry(e.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-[#5B616E]"><Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No diary entries</p></div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Diary Entry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Title</label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="diary-title-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Date</label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">Type</label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DIARY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={createEntry} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
